export const INSIGHTS_RANGE_PRESETS = ["7d", "28d", "90d", "365d"] as const;
export type InsightsRangePreset = (typeof INSIGHTS_RANGE_PRESETS)[number];

export const INSIGHTS_PAYOUT_TYPE_VALUES = {
  RPA: "rpa",
  RPC: "rpc",
  RPH: "rph",
} as const;
export const INSIGHTS_PAYOUT_TYPES = [
  INSIGHTS_PAYOUT_TYPE_VALUES.RPA,
  INSIGHTS_PAYOUT_TYPE_VALUES.RPC,
  INSIGHTS_PAYOUT_TYPE_VALUES.RPH,
] as const;
export type InsightsPayoutType = (typeof INSIGHTS_PAYOUT_TYPES)[number];
export const INSIGHTS_ACTIVITY_PAYOUT_FILTERS = [
  "all",
  ...INSIGHTS_PAYOUT_TYPES,
] as const;
export type InsightsActivityPayoutFilter =
  (typeof INSIGHTS_ACTIVITY_PAYOUT_FILTERS)[number];

export const INSIGHTS_SUMMARY_METRIC_KEYS = [
  "views",
  "clicks",
  "applications",
] as const;
export type InsightsSummaryMetricKey =
  (typeof INSIGHTS_SUMMARY_METRIC_KEYS)[number];

export const INSIGHTS_BREAKDOWN_KEYS = [
  "viewToClick",
  "clickToApplication",
  "overallConversion",
] as const;
export type InsightsBreakdownMetricKey =
  (typeof INSIGHTS_BREAKDOWN_KEYS)[number];

export const INSIGHTS_ACTIVITY_SUMMARY_KEYS = [
  "validClicks",
  "rpcClicks",
  "rpaClicks",
  "rphClicks",
  "approvedApplications",
  "approvedHires",
  "botClicks",
  "foreignClicks",
  "duplicateClicks",
  "earnings",
  "rpa",
  "rpc",
  "rph",
] as const;
export type InsightsActivitySummaryMetricKey =
  (typeof INSIGHTS_ACTIVITY_SUMMARY_KEYS)[number];

export const INSIGHTS_ACTIVITY_GRANULARITIES = [
  "day",
  "week",
  "month",
] as const;
export type InsightsActivityGranularity =
  (typeof INSIGHTS_ACTIVITY_GRANULARITIES)[number];

export interface InsightsSummaryMetric {
  key: InsightsSummaryMetricKey;
  value: number;
  change: number;
}

export interface InsightsBreakdownMetric {
  key: InsightsBreakdownMetricKey;
  value: number;
}

export interface InsightsActivitySummaryMetric {
  key: InsightsActivitySummaryMetricKey;
  value: number;
  change: number;
}

export interface InsightsAudience {
  totalAudience: number;
  audienceGrowth: number;
}

export interface InsightsAverageRpa {
  value: number;
  change: number;
}

export interface InsightsTopJob {
  jobId: string;
  refId: string | null;
  title: string;
  company: string;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  logo?: string | null;
  postedDate?: string | null;
  validClicks: number;
  approvedApplications: number;
  botClicks: number;
  foreignClicks: number;
  duplicateClicks: number;
  earnings: number;
  rpa: number;
  rpc: number;
  rph: number;
  payoutType: InsightsPayoutType | null;
  payoutValue: number | null;
}

export interface InsightsOverview {
  rangeLabel: string;
  comparisonLabel: string;
  summaryMetrics: InsightsSummaryMetric[];
  breakdown: InsightsBreakdownMetric[];
  averageRpa: InsightsAverageRpa;
  activitySummary: InsightsActivitySummaryMetric[];
  audience: InsightsAudience;
  topJobs: InsightsTopJob[];
  isEmpty: boolean;
}

export interface InsightsActivityBucket {
  label: string;
  startDate: string;
  endDate: string;
  validClicks: number;
  approvedApplications: number;
  botClicks: number;
  foreignClicks: number;
  duplicateClicks: number;
  earnings: number;
  rpa: number;
  rpc: number;
  rph: number;
}

export interface InsightsActivity {
  title: string;
  bucketGranularity: InsightsActivityGranularity;
  rows: InsightsActivityBucket[];
}

export interface InsightsJobs {
  rangeLabel: string;
  comparisonLabel: string;
  jobs: InsightsTopJob[];
}

export interface InsightsActivityMetricSnapshot {
  validClicks: number;
  rpcClicks: number;
  rpaClicks: number;
  rphClicks: number;
  approvedApplications: number;
  approvedHires: number;
  botClicks: number;
  foreignClicks: number;
  duplicateClicks: number;
  earnings: number;
  rpa: number;
  rpc: number;
  rph: number;
}

export interface InsightsQuery {
  rangePreset?: InsightsRangePreset;
  startDate?: string;
  endDate?: string;
  payoutType?: InsightsPayoutType;
}
